/**
 * QuantumFlowSync.AI - Security Settings Page
 * MFA setup, password policy, IP whitelist, break glass access
 */

import React, { useState, useEffect } from 'react';
import { 
  Shield, Key, Lock, Globe, AlertTriangle, Check, X,
  RefreshCw, Copy, QrCode, Clock, Plus, Trash2, Eye, EyeOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTier } from '../../hooks/usePermission';

const SecuritySettingsPage = () => {
  const { employee, apiRequest } = useAuth();
  const isSuperAdmin = useTier(1);
  
  // State
  const [activeTab, setActiveTab] = useState('mfa');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSecret, setMfaSecret] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [mfaToken, setMfaToken] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Password policy state
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  
  // IP whitelist state
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [newIpRange, setNewIpRange] = useState('');
  const [newIpDescription, setNewIpDescription] = useState('');
  
  // Break glass state
  const [breakGlassAccess, setBreakGlassAccess] = useState([]);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantForm, setGrantForm] = useState({
    employee_id: '',
    permissions: '',
    reason: '',
    duration: 4
  });
  
  // Fetch MFA status
  const fetchMFAStatus = async () => {
    try {
      const response = await apiRequest('/api/admin/security/mfa/status');
      if (response.success) {
        setMfaEnabled(response.data.enabled);
      }
    } catch (err) {
      console.error('Failed to fetch MFA status:', err);
    }
  };
  
  // Setup MFA
  const setupMFA = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/security/mfa/setup', {
        method: 'POST'
      });
      
      if (response.success) {
        setMfaSecret(response.data.secret);
        setQrCode(response.data.qrCode);
        setBackupCodes(response.data.backupCodes);
        setShowBackupCodes(true);
      } else {
        setError(response.error || 'Failed to setup MFA');
      }
    } catch (err) {
      setError('Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };
  
  // Enable MFA
  const enableMFA = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/security/mfa/enable', {
        method: 'POST',
        body: JSON.stringify({ token: mfaToken })
      });
      
      if (response.success) {
        setSuccess('MFA enabled successfully');
        setMfaEnabled(true);
        setMfaToken('');
      } else {
        setError(response.error || 'Failed to enable MFA');
      }
    } catch (err) {
      setError('Failed to enable MFA');
    } finally {
      setLoading(false);
    }
  };
  
  // Disable MFA
  const disableMFA = async () => {
    if (!confirm('Are you sure you want to disable MFA? This will reduce your account security.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/security/mfa/disable', {
        method: 'POST'
      });
      
      if (response.success) {
        setSuccess('MFA disabled successfully');
        setMfaEnabled(false);
        setMfaSecret(null);
        setQrCode(null);
        setBackupCodes([]);
      } else {
        setError(response.error || 'Failed to disable MFA');
      }
    } catch (err) {
      setError('Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch password policy
  const fetchPasswordPolicy = async () => {
    try {
      const response = await apiRequest('/api/admin/security/password-policy');
      if (response.success) {
        setPasswordPolicy(response.data.policy);
      }
    } catch (err) {
      console.error('Failed to fetch password policy:', err);
    }
  };
  
  // Fetch IP whitelist
  const fetchIPWhitelist = async () => {
    try {
      const response = await apiRequest('/api/admin/security/ip-whitelist');
      if (response.success) {
        setIpWhitelist(response.data.whitelist || []);
      }
    } catch (err) {
      console.error('Failed to fetch IP whitelist:', err);
    }
  };
  
  // Add IP to whitelist
  const addIPToWhitelist = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/security/ip-whitelist', {
        method: 'POST',
        body: JSON.stringify({
          ip_range: newIpRange,
          description: newIpDescription
        })
      });
      
      if (response.success) {
        setSuccess('IP added to whitelist successfully');
        setNewIpRange('');
        setNewIpDescription('');
        fetchIPWhitelist();
      } else {
        setError(response.error || 'Failed to add IP to whitelist');
      }
    } catch (err) {
      setError('Failed to add IP to whitelist');
    } finally {
      setLoading(false);
    }
  };
  
  // Remove IP from whitelist
  const removeIPFromWhitelist = async (id) => {
    if (!confirm('Are you sure you want to remove this IP from the whitelist?')) {
      return;
    }
    
    try {
      const response = await apiRequest(`/api/admin/security/ip-whitelist/${id}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        setSuccess('IP removed from whitelist successfully');
        fetchIPWhitelist();
      } else {
        setError(response.error || 'Failed to remove IP from whitelist');
      }
    } catch (err) {
      setError('Failed to remove IP from whitelist');
    }
  };
  
  // Fetch break glass access
  const fetchBreakGlassAccess = async () => {
    try {
      const response = await apiRequest('/api/admin/security/break-glass');
      if (response.success) {
        setBreakGlassAccess(response.data.access || []);
      }
    } catch (err) {
      console.error('Failed to fetch break glass access:', err);
    }
  };
  
  // Grant break glass access
  const grantBreakGlass = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/security/break-glass/grant', {
        method: 'POST',
        body: JSON.stringify({
          ...grantForm,
          permissions: grantForm.permissions.split(',').map(p => p.trim())
        })
      });
      
      if (response.success) {
        setSuccess('Break glass access granted successfully');
        setShowGrantModal(false);
        setGrantForm({ employee_id: '', permissions: '', reason: '', duration: 4 });
        fetchBreakGlassAccess();
      } else {
        setError(response.error || 'Failed to grant break glass access');
      }
    } catch (err) {
      setError('Failed to grant break glass access');
    } finally {
      setLoading(false);
    }
  };
  
  // Revoke break glass access
  const revokeBreakGlass = async (id) => {
    if (!confirm('Are you sure you want to revoke this break glass access?')) {
      return;
    }
    
    try {
      const response = await apiRequest(`/api/admin/security/break-glass/${id}/revoke`, {
        method: 'POST'
      });
      
      if (response.success) {
        setSuccess('Break glass access revoked successfully');
        fetchBreakGlassAccess();
      } else {
        setError(response.error || 'Failed to revoke break glass access');
      }
    } catch (err) {
      setError('Failed to revoke break glass access');
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
  };
  
  // Effect: Fetch data on tab change
  useEffect(() => {
    if (activeTab === 'mfa') {
      fetchMFAStatus();
    } else if (activeTab === 'password') {
      fetchPasswordPolicy();
    } else if (activeTab === 'ip-whitelist' && isSuperAdmin) {
      fetchIPWhitelist();
    } else if (activeTab === 'break-glass' && isSuperAdmin) {
      fetchBreakGlassAccess();
    }
  }, [activeTab]);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Security Settings
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage security features and access controls
        </p>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <span className="text-red-800 dark:text-red-200">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
          <span className="text-green-800 dark:text-green-200">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Tab Headers */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'mfa', label: 'Multi-Factor Auth', icon: Shield },
              { id: 'password', label: 'Password Policy', icon: Key },
              ...(isSuperAdmin ? [
                { id: 'ip-whitelist', label: 'IP Whitelist', icon: Globe },
                { id: 'break-glass', label: 'Break Glass', icon: AlertTriangle }
              ] : [])
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {/* MFA Tab */}
          {activeTab === 'mfa' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Multi-Factor Authentication (MFA)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Add an extra layer of security to your account with time-based one-time passwords (TOTP).
                </p>
              </div>
              
              {!mfaEnabled ? (
                <div className="space-y-4">
                  {!qrCode ? (
                    <button
                      onClick={setupMFA}
                      disabled={loading}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                    >
                      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                      Setup MFA
                    </button>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                          Step 1: Scan QR Code
                        </h4>
                        <div className="flex items-center justify-center mb-4">
                          <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                        </p>
                      </div>
                      
                      {showBackupCodes && backupCodes.length > 0 && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                            Backup Codes
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
                          </p>
                          <div className="grid grid-cols-2 gap-2 mb-4">
                            {backupCodes.map((code, index) => (
                              <div
                                key={index}
                                className="bg-white dark:bg-gray-800 rounded px-3 py-2 font-mono text-sm flex items-center justify-between"
                              >
                                {code}
                                <button onClick={() => copyToClipboard(code)}>
                                  <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <button
                            onClick={() => copyToClipboard(backupCodes.join('\n'))}
                            className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                          >
                            <Copy className="w-4 h-4" />
                            Copy All Codes
                          </button>
                        </div>
                      )}
                      
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                          Step 2: Verify Code
                        </h4>
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={mfaToken}
                            onChange={(e) => setMfaToken(e.target.value)}
                            placeholder="Enter 6-digit code"
                            maxLength={6}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                          />
                          <button
                            onClick={enableMFA}
                            disabled={loading || mfaToken.length !== 6}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                        <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          MFA is Enabled
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Your account is protected with multi-factor authentication
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={disableMFA}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Disable MFA
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Password Policy Tab */}
          {activeTab === 'password' && passwordPolicy && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Password Policy
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Current password requirements for all employees
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Minimum Length
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {passwordPolicy.minLength} characters
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Complexity
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Uppercase, lowercase, number, special char
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Expiry
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {passwordPolicy.expiryDays} days
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        History
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Cannot reuse last {passwordPolicy.historyCount} passwords
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* IP Whitelist Tab (Super Admin only) */}
          {activeTab === 'ip-whitelist' && isSuperAdmin && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    IP Whitelist
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Restrict login access to specific IP addresses or ranges
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                  Add New IP Range
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={newIpRange}
                    onChange={(e) => setNewIpRange(e.target.value)}
                    placeholder="192.168.1.0/24 or 192.168.1.1"
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <input
                    type="text"
                    value={newIpDescription}
                    onChange={(e) => setNewIpDescription(e.target.value)}
                    placeholder="Description (e.g., Office Network)"
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={addIPToWhitelist}
                    disabled={loading || !newIpRange}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    Add IP
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                {ipWhitelist.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No IP addresses in whitelist. All IPs are allowed.
                  </p>
                ) : (
                  ipWhitelist.map((ip) => (
                    <div
                      key={ip.id}
                      className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-lg p-4"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {ip.ip_range}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {ip.description}
                        </p>
                      </div>
                      <button
                        onClick={() => removeIPFromWhitelist(ip.id)}
                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          {/* Break Glass Tab (Super Admin only) */}
          {activeTab === 'break-glass' && isSuperAdmin && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Break Glass Emergency Access
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Grant temporary elevated permissions for emergency situations
                  </p>
                </div>
                <button
                  onClick={() => setShowGrantModal(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Grant Access
                </button>
              </div>
              
              <div className="space-y-3">
                {breakGlassAccess.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No active break glass access grants
                  </p>
                ) : (
                  breakGlassAccess.map((access) => (
                    <div
                      key={access.id}
                      className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {access.employee_name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Reason: {access.reason}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Expires in: {access.minutes_remaining} minutes
                          </p>
                        </div>
                        <button
                          onClick={() => revokeBreakGlass(access.id)}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecuritySettingsPage;
